import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  SCHOOL_CLASS_STANDARDS,
  classKey,
  classLabelFromKey,
  getSessionUser,
  getTeacherAssignedClass,
  getStudentsForClass,
  getDivisionsForStandard,
  saveStudentRecord,
  deleteStudentRecordById,
  updateStudentDataById,
} from "@/lib/eisStore";
import {
  addStudent as addStudentToSupabase,
  deleteStudentFromSupabase,
  getStudentsForClassFromSupabase,
  isSupabaseConfigured,
  updateStudentInSupabase,
} from "@/lib/supabaseOps";

const ADD_FORM = {
  name: "",
  motherName: "",
  fatherName: "",
  phone: "",
  rollNo: "",
  standard: "",
  division: "",
  transportMode: "",
  address: "",
};

const EDIT_FORM = {
  studentName: "",
  name: "",
  standard: "",
  division: "",
  phone: "",
  motherName: "",
  fatherName: "",
  rollNo: "",
  transportMode: "",
  address: "",
  dob: "",
  bloodGroup: "",
  emergencyContact: "",
};

function DetailItem({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</p>
      <p className="mt-1 text-sm font-medium text-slate-800 break-words">{value || "N/A"}</p>
    </div>
  );
}

function sortStudents(students) {
  return [...students].sort((a, b) => {
    const rollA = Number(a.rollNo) || Number.MAX_SAFE_INTEGER;
    const rollB = Number(b.rollNo) || Number.MAX_SAFE_INTEGER;
    if (rollA !== rollB) return rollA - rollB;
    return (a.name || a.studentName || "").localeCompare(b.name || b.studentName || "");
  });
}

function TeacherStudentData() {
  const user = getSessionUser() || {};
  const assignedClass = getTeacherAssignedClass(user);
  const [form, setForm] = useState(ADD_FORM);
  const [rosterStudents, setRosterStudents] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const rosterLabel = assignedClass ? classLabelFromKey(assignedClass.key) : "";
  const addDivisions = getDivisionsForStandard(form.standard);

  useEffect(() => {
    let active = true;

    const loadRoster = async () => {
      if (!assignedClass?.key) {
        setRosterStudents([]);
        return;
      }

      setRosterLoading(true);
      try {
        const students = isSupabaseConfigured
          ? await getStudentsForClassFromSupabase(assignedClass.key)
          : getStudentsForClass(assignedClass.key);
        if (active) {
          setRosterStudents(sortStudents(students));
        }
      } catch (error) {
        console.error(error);
        if (active) {
          setRosterStudents(sortStudents(getStudentsForClass(assignedClass.key)));
        }
      } finally {
        if (active) {
          setRosterLoading(false);
        }
      }
    };

    loadRoster();
    return () => {
      active = false;
    };
  }, [assignedClass?.key, refreshTick]);

  const removeStudent = (student) => {
    if (!window.confirm(`Delete ${student.studentName || student.name}? This cannot be undone.`)) {
      return;
    }
    const deleteStudent = async () => {
      try {
        if (isSupabaseConfigured) {
          await deleteStudentFromSupabase(student.id);
        }
        const deleted = deleteStudentRecordById(student.id);
        if (!deleted) {
          toast.error("Could not delete the student");
          return;
        }
        setRefreshTick((current) => current + 1);
        toast.success("Student deleted");
      } catch (error) {
        console.error(error);
        toast.error(error?.message || "Could not delete the student");
      }
    };

    deleteStudent();
  };

  const addStudent = async () => {
    if (
      !form.name.trim() ||
      !form.motherName.trim() ||
      !form.fatherName.trim() ||
      !form.phone.trim() ||
      !form.rollNo.toString().trim() ||
      !form.standard ||
      !form.division ||
      !form.transportMode.trim() ||
      !form.address.trim()
    ) {
      toast.error("Please fill in all student details");
      return;
    }

    if (isSupabaseConfigured) {
      try {
        await addStudentToSupabase({
          studentName: form.name.trim(),
          motherName: form.motherName.trim(),
          fatherName: form.fatherName.trim(),
          phoneNumber: form.phone.trim(),
          rollNumber: form.rollNo.trim(),
          standard: form.standard,
          division: form.division,
          modeOfTransport: form.transportMode.trim(),
          address: form.address.trim(),
        });
      } catch (error) {
        console.error(error);
        toast.error(error?.message || "Could not save the student to the database");
        return;
      }
    }

    const saved = saveStudentRecord({
      name: form.name.trim(),
      studentName: form.name.trim(),
      motherName: form.motherName.trim(),
      fatherName: form.fatherName.trim(),
      phone: form.phone.trim(),
      rollNo: form.rollNo.trim(),
      standard: form.standard,
      division: form.division,
      transportMode: form.transportMode.trim(),
      address: form.address.trim(),
    });

    if (!saved) {
      toast.error("Could not add the student");
      return;
    }

    setForm((current) => ({
      ...ADD_FORM,
      standard: current.standard,
      division: current.division,
    }));
    setRefreshTick((current) => current + 1);
    toast.success("Student added");
  };

  return (
    <AppShell>
      <div data-testid="student-data-page" className="space-y-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
            Student Data
          </h2>
          <p className="text-slate-500 mt-1 text-sm">
            Add a new student record here. Email is added later when the student signs up.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_0.95fr] gap-6">
          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>Add Student</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Student Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                  className="mt-1"
                  placeholder="Full name"
                />
              </div>

              <div>
                <Label>Mother Name</Label>
                <Input
                  value={form.motherName}
                  onChange={(e) => setForm((current) => ({ ...current, motherName: e.target.value }))}
                  className="mt-1"
                  placeholder="Mother's name"
                />
              </div>

              <div>
                <Label>Father Name</Label>
                <Input
                  value={form.fatherName}
                  onChange={(e) => setForm((current) => ({ ...current, fatherName: e.target.value }))}
                  className="mt-1"
                  placeholder="Father's name"
                />
              </div>

              <div>
                <Label>Phone Number</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm((current) => ({ ...current, phone: e.target.value }))}
                  className="mt-1"
                  placeholder="Phone number"
                />
              </div>

              <div>
                <Label>Roll Number</Label>
                <Input
                  type="number"
                  value={form.rollNo}
                  onChange={(e) => setForm((current) => ({ ...current, rollNo: e.target.value }))}
                  className="mt-1"
                  placeholder="Roll number"
                />
              </div>

              <div>
                <Label>Standard</Label>
                <Select
                  value={form.standard}
                  onValueChange={(value) =>
                    setForm((current) => ({ ...current, standard: value, division: "" }))
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent>
                    {SCHOOL_CLASS_STANDARDS.map((item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Division</Label>
                <Select
                  value={form.division}
                  onValueChange={(value) => setForm((current) => ({ ...current, division: value }))}
                  disabled={!form.standard}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select division" />
                  </SelectTrigger>
                  <SelectContent>
                    {addDivisions.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label>Mode of Transport</Label>
                <Input
                  value={form.transportMode}
                  onChange={(e) => setForm((current) => ({ ...current, transportMode: e.target.value }))}
                  className="mt-1"
                  placeholder="Bus, walk, van, etc."
                />
              </div>

              <div className="md:col-span-2">
                <Label>Address</Label>
                <Textarea
                  value={form.address}
                  onChange={(e) => setForm((current) => ({ ...current, address: e.target.value }))}
                  className="mt-1 min-h-28"
                  placeholder="Home address"
                />
              </div>

              <div className="md:col-span-2 flex justify-end">
                <Button
                  type="button"
                  onClick={addStudent}
                  className="rounded-full text-white"
                  style={{ backgroundColor: "#0b1f7a" }}
                >
                  Add Student
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
            <CardTitle style={{ color: "#0b1f7a" }}>
              Student Roster {rosterLabel ? `for ${rosterLabel}` : ""}
            </CardTitle>
          </CardHeader>
            <CardContent className="space-y-3">
              {assignedClass ? (
                rosterLoading ? (
                  <p className="text-sm text-slate-500">Loading students...</p>
                ) : rosterStudents.length ? (
                  rosterStudents.map((student) => (
                    <div key={student.id} className="rounded-xl border border-slate-200 p-4 space-y-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-800">{student.studentName || student.name}</p>
                          <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600 inline-flex mt-2">
                            {student.email ? "Linked" : "Pending"}
                          </span>
                        </div>
                        <Button
                          type="button"
                          onClick={() => removeStudent(student)}
                          className="rounded-full text-white"
                          style={{ backgroundColor: "#dc2626" }}
                        >
                          Delete
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">
                        Roll {student.rollNo || "N/A"} · {student.standard} {student.division}
                      </p>
                      <p className="text-xs text-slate-500">{student.phone || "No phone added"}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No students found for your class yet.</p>
                )
              ) : (
                <p className="text-sm text-slate-500">
                  You are not assigned as a class teacher, so no class roster is available.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

function AdminStudentData() {
  const [standard, setStandard] = useState("");
  const [division, setDivision] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [form, setForm] = useState(EDIT_FORM);
  const [classStudents, setClassStudents] = useState([]);
  const [classLoading, setClassLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let active = true;

    const loadClassStudents = async () => {
      if (!standard || !division) {
        setClassStudents([]);
        return;
      }

      setClassLoading(true);
      try {
        const students = isSupabaseConfigured
          ? await getStudentsForClassFromSupabase(classKey(standard, division))
          : getStudentsForClass(classKey(standard, division));
        if (active) {
          setClassStudents(sortStudents(students));
        }
      } catch (error) {
        console.error(error);
        if (active) {
          setClassStudents(sortStudents(getStudentsForClass(classKey(standard, division))));
        }
      } finally {
        if (active) {
          setClassLoading(false);
        }
      }
    };

    loadClassStudents();
    return () => {
      active = false;
    };
  }, [standard, division, refreshTick]);

  const selectedStudent = classStudents.find((student) => student.id === selectedStudentId) || null;

  const handleStandardChange = (value) => {
    setStandard(value);
    setDivision("");
    setSelectedStudentId("");
  };

  const handleDivisionChange = (value) => {
    setDivision(value);
    setSelectedStudentId("");
  };

  const pickStudent = (student) => {
    setSelectedStudentId(student.id);
    setForm({
      studentName: student.studentName || student.name || "",
      name: student.name || "",
      standard: student.standard || "",
      division: student.division || "",
      phone: student.phone || "",
      motherName: student.motherName || "",
      fatherName: student.fatherName || "",
      rollNo: student.rollNo || "",
      transportMode: student.transportMode || "",
      address: student.address || "",
      dob: student.dob || "",
      bloodGroup: student.bloodGroup || "",
      emergencyContact: student.emergencyContact || "",
    });
  };

  const save = () => {
    if (!selectedStudent) {
      toast.error("Please choose a student");
      return;
    }
    const nextStudent = {
      ...selectedStudent,
      ...form,
      name: form.studentName || form.name,
      studentName: form.studentName || form.name,
    };
    const hasChanges = Object.keys(nextStudent).some((key) => {
      const before = selectedStudent?.[key];
      const after = nextStudent?.[key];
      return String(before ?? "") !== String(after ?? "");
    });
    if (!hasChanges) {
      toast("No changes to save");
      return;
    }

    const saveStudent = async () => {
      try {
        if (isSupabaseConfigured) {
          await updateStudentInSupabase(selectedStudent.id, nextStudent);
        }
        const updatedStudent = updateStudentDataById(selectedStudent.id, nextStudent);
        if (updatedStudent) {
          setSelectedStudentId(updatedStudent.id);
          setStandard(updatedStudent.standard || standard);
          setDivision(updatedStudent.division || division);
          setForm({
            studentName: updatedStudent.studentName || updatedStudent.name || "",
            name: updatedStudent.name || "",
            standard: updatedStudent.standard || "",
            division: updatedStudent.division || "",
            phone: updatedStudent.phone || "",
            motherName: updatedStudent.motherName || "",
            fatherName: updatedStudent.fatherName || "",
            rollNo: updatedStudent.rollNo || "",
            transportMode: updatedStudent.transportMode || "",
            address: updatedStudent.address || "",
            dob: updatedStudent.dob || "",
            bloodGroup: updatedStudent.bloodGroup || "",
            emergencyContact: updatedStudent.emergencyContact || "",
          });
        }
        setRefreshTick((current) => current + 1);
        toast.success("Student details updated");
      } catch (error) {
        console.error(error);
        toast.error(error?.message || "Could not update student details");
      }
    };

    saveStudent();
  };

  return (
    <AppShell>
      <div data-testid="student-data-page" className="space-y-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
            Student Data
          </h2>
          <p className="text-slate-500 mt-1 text-sm">
            Select a class and section to view or edit student records.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-6">
          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>Choose Class</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Class</Label>
                <Select value={standard} onValueChange={handleStandardChange}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent>
                    {SCHOOL_CLASS_STANDARDS.map((item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Section</Label>
                <Select value={division} onValueChange={handleDivisionChange} disabled={!standard}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select section" />
                  </SelectTrigger>
                  <SelectContent>
                    {getDivisionsForStandard(standard).map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <p className="text-sm text-slate-500">
                {standard && division ? classLabelFromKey(classKey(standard, division)) : "Pick a class and section"}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>
                Students {standard && division ? `in ${classLabelFromKey(classKey(standard, division))}` : ""}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {!standard || !division ? (
                <p className="text-sm text-slate-500">Choose a class to see the student list.</p>
              ) : classLoading ? (
                <p className="text-sm text-slate-500">Loading students...</p>
              ) : classStudents.length ? (
                classStudents.map((student) => (
                  <button
                    key={student.id}
                    type="button"
                    onClick={() => pickStudent(student)}
                    className={`w-full text-left rounded-xl border px-4 py-3 transition ${
                      selectedStudentId === student.id
                        ? "border-[#0b1f7a] bg-[#EEF0FA]"
                        : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-800">{student.studentName || student.name}</p>
                        <p className="text-xs text-slate-500">
                          Roll {student.rollNo} · {student.email || "No email yet"}
                        </p>
                      </div>
                      <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                        {student.standard} {student.division}
                      </span>
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-sm text-slate-500">No students found in this class.</p>
              )}
            </CardContent>
          </Card>
        </div>

        {selectedStudent && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle style={{ color: "#0b1f7a" }}>Selected Student Details</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <DetailItem label="Student Name" value={selectedStudent.studentName || selectedStudent.name} />
                <DetailItem label="Class" value={selectedStudent.standard || "N/A"} />
                <DetailItem label="Division" value={selectedStudent.division || "N/A"} />
                <DetailItem label="Roll Number" value={selectedStudent.rollNo || "N/A"} />
                <DetailItem label="Email" value={selectedStudent.email || "N/A"} />
                <DetailItem label="Phone Number" value={selectedStudent.phone || "N/A"} />
                <DetailItem label="Mother's Name" value={selectedStudent.motherName || "N/A"} />
                <DetailItem label="Father's Name" value={selectedStudent.fatherName || "N/A"} />
                <DetailItem label="Mode of Transport" value={selectedStudent.transportMode || "N/A"} />
                <DetailItem label="Date of Birth" value={selectedStudent.dob || "N/A"} />
                <DetailItem label="Blood Group" value={selectedStudent.bloodGroup || "N/A"} />
                <DetailItem label="Emergency Contact" value={selectedStudent.emergencyContact || "N/A"} />
                <div className="md:col-span-2 xl:col-span-3 rounded-xl border border-slate-200 bg-white p-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Address</p>
                  <p className="mt-1 text-sm font-medium text-slate-800 whitespace-pre-wrap">
                    {selectedStudent.address || "N/A"}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle style={{ color: "#0b1f7a" }}>Edit Student Details</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Student Name</Label>
                  <Input
                    value={form.studentName}
                    onChange={(e) => setForm((current) => ({ ...current, studentName: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm((current) => ({ ...current, phone: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Roll Number</Label>
                  <Input
                    type="number"
                    value={form.rollNo}
                    onChange={(e) => setForm((current) => ({ ...current, rollNo: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Mother's Name</Label>
                  <Input
                    value={form.motherName}
                    onChange={(e) => setForm((current) => ({ ...current, motherName: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Father's Name</Label>
                  <Input
                    value={form.fatherName}
                    onChange={(e) => setForm((current) => ({ ...current, fatherName: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Standard</Label>
                  <Select
                    value={form.standard}
                    onValueChange={(value) =>
                      setForm((current) => ({ ...current, standard: value, division: "" }))
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                    <SelectContent>
                      {SCHOOL_CLASS_STANDARDS.map((item) => (
                        <SelectItem key={item} value={item}>
                          {item}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Division</Label>
                  <Select
                    value={form.division}
                    onValueChange={(value) => setForm((current) => ({ ...current, division: value }))}
                    disabled={!form.standard}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select division" />
                    </SelectTrigger>
                    <SelectContent>
                      {getDivisionsForStandard(form.standard).map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Mode of Transport</Label>
                  <Input
                    value={form.transportMode}
                    onChange={(e) => setForm((current) => ({ ...current, transportMode: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Address</Label>
                  <Textarea
                    value={form.address}
                    onChange={(e) => setForm((current) => ({ ...current, address: e.target.value }))}
                    className="mt-1 min-h-28"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={form.dob}
                    onChange={(e) => setForm((current) => ({ ...current, dob: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Blood Group</Label>
                  <Input
                    value={form.bloodGroup}
                    onChange={(e) => setForm((current) => ({ ...current, bloodGroup: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Emergency Contact</Label>
                  <Input
                    value={form.emergencyContact}
                    onChange={(e) => setForm((current) => ({ ...current, emergencyContact: e.target.value }))}
                    className="mt-1"
                  />
                </div>

                <div className="md:col-span-2 flex justify-end">
                  <Button
                    type="button"
                    onClick={save}
                    className="rounded-full text-white"
                    style={{ backgroundColor: "#0b1f7a" }}
                  >
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AppShell>
  );
}

export default function StudentData() {
  const user = getSessionUser();

  if (user?.role === "teacher") {
    return <TeacherStudentData />;
  }

  return <AdminStudentData />;
}
